/**
 * The publish gate (TED #2467) as the pages read it.
 *
 * Until Peter publishes the ORTEX dividend forecasts (everywhere, or country by country), a caller
 * who is not staff gets declared dividends and history only, every forecast field null. Every
 * payload and grid response says what this caller may see: `published` (forecasts everywhere) and
 * `published_countries` (`null` when everywhere, else the ISO2 venue countries shown, possibly
 * none). Staff always get `published: true`, so they see every column and box as before.
 *
 * Where nothing is visible the pages hide what would only be blank (the forecast columns, the
 * forecast-only tabs and boxes) and say once, plainly, that forecasts are coming. Where some
 * countries are published the columns stay and rows of the other countries show a dash.
 */
import { createContext, FC, useContext } from "react";
import { DivAccess } from "@/models/dividends-v2";
import irStyles from "@/components/index-rebalance-v2/IndexRebalanceV2.module.scss";

export interface DivPublication {
    /** Forecasts visible in every country (staff, or published everywhere). */
    everywhere: boolean;
    /** ISO2 venue countries whose forecasts are visible, when not everywhere. */
    countries: string[];
}

export const PUBLISHED_EVERYWHERE: DivPublication = { everywhere: true, countries: [] };

type PublicationFields = Pick<DivAccess, "published" | "published_countries">;
/** The camelCase pair a grid response carries. */
type GridPublicationFields = { published?: boolean; publishedCountries?: string[] | null };

/**
 * What a payload says this caller may see. A payload without the pair (an older TED, or a fixture
 * built before the gate) reads as published: the server nulls what it hides either way.
 */
export const publicationOf = (access: PublicationFields | GridPublicationFields | null | undefined): DivPublication => {
    if (!access) return PUBLISHED_EVERYWHERE;
    const published = (access as PublicationFields).published;
    const countries = (access as PublicationFields).published_countries ?? (access as GridPublicationFields).publishedCountries ?? null;
    if (published === undefined || published === true) return PUBLISHED_EVERYWHERE;
    return { everywhere: false, countries: (countries ?? []).map((c) => String(c).toUpperCase()) };
};

/** Some forecast is visible to this caller somewhere. */
export const forecastsAnywhere = (pub: DivPublication): boolean => pub.everywhere || pub.countries.length > 0;

/** Forecasts of a listing in `country` (ISO2 venue) are visible to this caller. */
export const forecastsFor = (pub: DivPublication, country: string | null | undefined): boolean => pub.everywhere || (!!country && pub.countries.includes(country.toUpperCase()));

/** The section shell's reading of `overview`, for the tabs and boxes below it. */
export const DivPublicationContext = createContext<DivPublication>(PUBLISHED_EVERYWHERE);

export const useDivPublication = (): DivPublication => useContext(DivPublicationContext);

const regionName = (code: string): string => {
    try {
        return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code;
    } catch {
        return code;
    }
};

/** "United States", "United States and Canada", "United States, Canada and Japan". */
export const countryList = (codes: string[]): string => {
    const names = codes.map((c) => (c === "US" ? "the US" : c === "GB" ? "the UK" : regionName(c)));
    return names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

/** The line shown once where forecasts will appear. No product mechanics in the wording, by rule. */
export const comingSoonText = (pub: DivPublication, scope: "section" | "stock" = "section"): string | null => {
    if (pub.everywhere) return null;
    if (scope === "stock") return "ORTEX forecasts for this market are coming soon.";
    if (!pub.countries.length) return "ORTEX dividend forecasts are coming soon. Until then this page shows declared dividends, their dates and their history.";
    return `ORTEX dividend forecasts are shown for ${countryList(pub.countries)}. Forecasts for other markets are coming soon.`;
};

/** The neutral "coming soon" line, in the grid-note style the section uses for its other notes. */
export const ForecastsComingSoon: FC<{ pub: DivPublication; scope?: "section" | "stock"; className?: string }> = ({ pub, scope = "section", className }) => {
    const text = comingSoonText(pub, scope);
    if (!text) return null;
    return (
        <div className={className ?? irStyles.irGridNote} role="note" data-testid="dividends-coming-soon">
            <span>{text}</span>
        </div>
    );
};

/** A grid response's columns as an unpublished caller should see them: `fields` dropped, with their chips and sort. */
export const withoutColumns = <T extends Record<string, any>>(res: T, fields: readonly string[]): T => {
    if (!res || !Array.isArray(res.columns)) return res;
    const drop = new Set(fields);
    const visibility = res.columnVisibilityModel ? Object.fromEntries(Object.entries(res.columnVisibilityModel).filter(([k]) => !drop.has(k))) : res.columnVisibilityModel;
    const sortModel = res.sortModel && Object.keys(res.sortModel).some((k) => drop.has(k)) ? null : res.sortModel;
    return { ...res, columns: res.columns.filter((c: { field: string }) => !drop.has(c.field)), columnVisibilityModel: visibility, sortModel };
};

/** A grid response with `fields` shown by default (taken out of the hidden-by-default model). */
export const showColumns = <T extends Record<string, any>>(res: T, fields: readonly string[]): T =>
    !res?.columnVisibilityModel ? res : { ...res, columnVisibilityModel: Object.fromEntries(Object.entries(res.columnVisibilityModel).filter(([k]) => !fields.includes(k))) };

/** Events-grid columns that only ever hold a forecast (`unpublished.FORECAST_FIELDS` on TED). */
export const EVENT_FORECAST_COLUMNS = [
    "amountP50",
    "amountP10",
    "amountP90",
    "changePct",
    "outcomeMix",
    "outcome",
    "outcomeProbability",
    "optionsImplied",
    "forwardYieldPct",
    "expectedDropPct",
    "eventYieldPct",
    "exDropRatio",
    "changeChancePct",
    "cutChancePct",
    "specialChancePct",
    "radarScore",
    "pRaise",
    "pHold",
    "pCut",
    "pOmit",
    "pSpecial",
    "declaredVsForecast",
    "declarationBandDays",
    "exBandDays",
    "dateConfidence",
    "drivers",
    "daysLate",
] as const;

/** Screener columns that are forecasts (`unpublished.LISTING_FORECAST_FIELDS`). */
export const SCREENER_FORECAST_COLUMNS = ["nextAmount", "forwardYieldPct", "changeChancePct", "cutChance12mPct"] as const;
